import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import PublicFooter from "@/components/PublicFooter";

/**
 * Public legal pages (store-compliance bundle).
 * Owner-confirmed values (2026-09-21): support contact support@plusmaker.club;
 * governing law Malaysia; Lucky Draw minimum age 18+; no restricted countries specified.
 * Financial-record retention wording is owner-approved (anonymised form, no fixed period).
 * All other statements reflect the existing codebase and current operations only.
 */

function LegalLayout({ title, updated, children, testId }) {
  return (
    <div className="min-h-screen bg-[#050506] text-white" data-testid={testId}>
      <div className="border-b border-[#222530]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <Link to="/" aria-label="PlusMaker home"><Logo /></Link>
          <Link to="/login" className="text-xs text-[#D4AF37] hover:text-[#E5C158] transition-colors" data-testid={`${testId}-login-link`}>Member Login</Link>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <p className="eyebrow-label mb-2">Legal</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">{title}</h1>
        <p className="text-xs text-[#64748B] mb-10">Last updated: {updated}</p>
        <div className="space-y-8 text-sm text-[#94A3B8] leading-relaxed">{children}</div>
      </div>
      <PublicFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-base font-semibold text-white mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

const UPDATED = "21 September 2026";

export function PrivacyPolicyPage() {
  return (
    <LegalLayout title="Privacy Policy" updated={UPDATED} testId="privacy-page">
      <Section title="1. Who We Are">
        <p>PLUSMAKER is a private, referral-based member ecosystem offering trading tools, education, rewards and related member services. This policy explains what information we collect, why, and the choices you have.</p>
      </Section>
      <Section title="2. Information We Collect">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Account details: username, full name, email address and password (stored only as a bcrypt hash — never in plain text).</li>
          <li>Profile details you provide: phone number, country, registered broker, delivery address and profile avatar.</li>
          <li>Activity records: rank progression, reward redemptions, product access, purchases, support conversations and notification reads.</li>
          <li>Device tokens, used only to deliver push notifications you opt into.</li>
          <li>Session data: secure, httpOnly authentication cookies that keep you signed in.</li>
        </ul>
      </Section>
      <Section title="3. How We Use Information">
        <p>We use your information to operate your account, provide the products and features you unlock, fulfil physical rewards, send service notifications, and keep the platform secure (including brute-force protection on sign-in).</p>
      </Section>
      <Section title="4. Who Can See Your Information">
        <p>We do not sell your personal data. Your sponsoring Introducing Broker can see limited client information (name, username, email and rank status) for members they referred. Platform administrators access member data only to operate, support and moderate the service.</p>
      </Section>
      <Section title="5. Account Deletion">
        <p>You can request permanent deletion of your account at any time — see our <Link to="/deletion" className="text-[#D4AF37] hover:text-[#E5C158]" data-testid="privacy-deletion-link">Account Deletion information</Link>. When a deletion request is processed, your operational data is deleted. Certain financial and transaction records may be retained in anonymised form only where required for legal, accounting, tax, fraud-prevention or dispute-resolution purposes. Personal identifiers are removed where reasonably possible.</p>
      </Section>
      <Section title="6. Security">
        <p>Passwords are hashed with bcrypt, sessions use short-lived httpOnly tokens, and sign-in is protected by attempt throttling. No method of transmission or storage is 100% secure, but we apply these safeguards consistently.</p>
      </Section>
      <Section title="7. Contact">
        <p>Members can reach us anytime through the in-app PLUSMAKER SUPPORT assistant, and anyone can contact us at <span className="text-white">support@plusmaker.club</span>.</p>
      </Section>
    </LegalLayout>
  );
}

export function TermsOfServicePage() {
  return (
    <LegalLayout title="Terms of Service" updated={UPDATED} testId="terms-page">
      <Section title="1. The Service">
        <p>PLUSMAKER provides a private member ecosystem including trading indicators, expert advisors, educational content (Live &amp; Learn), a member rewards program, a COINS credit system and subscription plans. Access is referral-based through authorized PLUSMAKER partners.</p>
      </Section>
      <Section title="2. Not Financial Advice">
        <p>PLUSMAKER provides tooling and education — not financial, investment or trading advice. Nothing on this platform is a recommendation to buy or sell any instrument. Trading foreign exchange, commodities and CFDs carries a high level of risk and may not be suitable for all investors. Past performance is not indicative of future results. You should never trade with capital you cannot afford to lose.</p>
      </Section>
      <Section title="3. Accounts">
        <p>You must provide accurate registration information and keep your credentials confidential. Accounts are personal and non-transferable. We may suspend accounts that breach these terms or abuse platform systems.</p>
      </Section>
      <Section title="4. COINS, Payments and Subscriptions">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>COINS are platform credits purchased in USD (1 USD = 1 COINS) via our payment processor, Stripe. COINS are not cash, carry no cash value, and are non-transferable.</li>
          <li>Purchases made with COINS or by card are final and non-refundable, as acknowledged at checkout.</li>
          <li>Subscription plans (FREE, PRO, ADVANCE) activate after confirmed payment and renew per the displayed billing period. Plan features and pricing are shown before you pay.</li>
          <li>COINS balances are forfeited if your account is deleted — deletion is permanent and balances are not recoverable.</li>
        </ul>
      </Section>
      <Section title="5. Rewards and Lucky Draw">
        <p>Rewards and Lucky Draw participation are governed by program rules shown in the member area, including the <Link to="/lucky-draw-rules" className="text-[#D4AF37] hover:text-[#E5C158]" data-testid="terms-luckydraw-link">Lucky Draw Official Rules</Link>. Physical rewards require a valid delivery address.</p>
      </Section>
      <Section title="6. Acceptable Use">
        <p>You agree not to abuse, scrape, reverse-engineer or disrupt the platform, attempt to access other members' data, or use the service for unlawful purposes.</p>
      </Section>
      <Section title="7. Limitation of Liability">
        <p>The service is provided "as is" without warranties of any kind. To the maximum extent permitted by law, PLUSMAKER is not liable for trading losses, lost profits, or indirect or consequential damages arising from use of the platform.</p>
      </Section>
      <Section title="8. Changes and Contact">
        <p>We may update these terms; material changes will be announced in the member area. Continued use after changes constitutes acceptance. These terms are governed by the laws of Malaysia. Members can contact us via the in-app PLUSMAKER SUPPORT assistant or at <span className="text-white">support@plusmaker.club</span>.</p>
      </Section>
    </LegalLayout>
  );
}

export function AccountDeletionPage() {
  return (
    <LegalLayout title="Account Deletion" updated={UPDATED} testId="deletion-info-page">
      <Section title="How to Request Deletion">
        <p>You can request permanent deletion of your PLUSMAKER account at any time, from the web member area or the mobile app:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Web: sign in → Profile → Delete My Account.</li>
          <li>Mobile app: use the account-deletion option in your member profile.</li>
        </ul>
        <p>You will be asked to re-enter your current password and type DELETE to confirm. Your account is <span className="text-white font-medium">not deleted immediately</span> — an administrator reviews and processes every request.</p>
      </Section>
      <Section title="What Is Deleted">
        <p>Your profile, rank progression, certificates, reward redemptions, indicator and expert-advisor access, COINS balance and purchases, Live &amp; Learn bookings, lucky-draw tickets and prizes, notifications, support history, devices and uploaded files.</p>
      </Section>
      <Section title="What Is Retained">
        <p>Financial records required for accounting (payment and wallet transactions) are retained in <span className="text-white font-medium">anonymised form</span>. Certain financial and transaction records may be retained in anonymised form only where required for legal, accounting, tax, fraud-prevention or dispute-resolution purposes. Personal identifiers are removed where reasonably possible.</p>
      </Section>
      <Section title="This Is Permanent">
        <p>Deletion is final. Your membership, COINS balance, rewards, certificates, entitlements and all product access cannot be recovered once the request is processed.</p>
      </Section>
      <Section title="Questions">
        <p>Members can ask about deletion through the in-app PLUSMAKER SUPPORT assistant or at <span className="text-white">support@plusmaker.club</span> before submitting a request.</p>
      </Section>
    </LegalLayout>
  );
}

export function LuckyDrawRulesPage() {
  return (
    <LegalLayout title="Lucky Draw — Official Rules" updated={UPDATED} testId="lucky-draw-rules-page">
      <Section title="1. How to Enter">
        <p>Entries (tickets) are earned through rank progress: tickets are awarded automatically when a member reaches the rank milestone attached to each draw tier. <span className="text-white font-medium">No purchase is necessary to enter or win.</span> Making a purchase does not improve your chances of winning.</p>
      </Section>
      <Section title="2. How Draws Work">
        <p>Each spin uses one ticket. Every result is decided securely by our servers before the wheel animation plays, and every spin is recorded. Prize stock is limited and shown per draw tier.</p>
      </Section>
      <Section title="3. Eligibility">
        <p>Participation is open to PLUSMAKER members whose accounts are in good standing and who are 18 years of age or above. No restricted countries are currently specified; eligibility and geographic restrictions may apply where required by law. We may void entries obtained through abuse or system manipulation.</p>
      </Section>
      <Section title="4. Prizes">
        <p>Prizes are physical items fulfilled to the delivery address you confirm after winning. Prizes and availability are subject to these official rules and may be substituted with items of equal value if unavailable. Prizes are non-transferable and cannot be exchanged for cash or COINS.</p>
      </Section>
      <Section title="5. No Third-Party Sponsorship">
        <p>Apple and Google are not sponsors, endorsers or administrators of, and are not involved in, this promotion in any way. The Lucky Draw is operated solely by PLUSMAKER.</p>
      </Section>
      <Section title="6. General">
        <p>Operator decisions on eligibility, entries and prize fulfilment are final. These rules may be updated; material changes will be announced in the member area. Questions: members can use the in-app PLUSMAKER SUPPORT assistant or contact <span className="text-white">support@plusmaker.club</span>.</p>
      </Section>
    </LegalLayout>
  );
}
